import { ArrowDown, ArrowUp, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { getWaidsMap, saveWaidsMap } from "@/features/admin/api";

interface Row {
  original: string;
  replacement: string;
}

/**
 * WAIDS Mappings (`/admin/waidsmap`) — Wage & Income Summary category remap
 * table. Sortable rows + add-row; Save replaces the whole table atomically.
 */
export default function WaidsMapPage() {
  const [rows, setRows] = useState<Row[]>([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    getWaidsMap()
      .then((data) => setRows(data.map((m) => ({ original: m.original, replacement: m.replacement }))))
      .catch(() => toast.error("Couldn't load the WAIDS map."));
  }, []);

  function update(index: number, field: keyof Row, value: string) {
    setRows((r) => r.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
  }

  function move(index: number, direction: -1 | 1) {
    const target = index + direction;
    if (target < 0 || target >= rows.length) return;
    setRows((r) => {
      const next = [...r];
      [next[index], next[target]] = [next[target]!, next[index]!];
      return next;
    });
  }

  async function save() {
    const cleaned = rows.filter((r) => r.original.trim());
    setBusy(true);
    try {
      const saved = await saveWaidsMap(
        cleaned.map((r) => ({ original: r.original.trim(), replacement: r.replacement.trim() })),
      );
      setRows(saved.map((m) => ({ original: m.original, replacement: m.replacement })));
      toast.success("WAIDS map saved.");
    } catch {
      toast.error("Couldn't save. Every row needs an original value.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-start justify-between gap-4">
        <div>
          <CardTitle>WAIDS Mappings</CardTitle>
          <CardDescription>
            Normalize new-format IRS Wage & Income category text → the canonical older text.
          </CardDescription>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="gap-1.5"
          onClick={() => setRows((r) => [...r, { original: "", replacement: "" }])}
        >
          <Plus className="size-4" /> Add mapping
        </Button>
      </CardHeader>
      <CardContent className="grid gap-3">
        {rows.length === 0 ? (
          <p className="text-muted-foreground text-sm">No mappings yet.</p>
        ) : (
          rows.map((row, index) => (
            <div key={index} className="flex items-center gap-2">
              <Input
                aria-label={`Original ${index + 1}`}
                placeholder="Original category text"
                value={row.original}
                onChange={(e) => update(index, "original", e.target.value)}
              />
              <span className="text-muted-foreground shrink-0">→</span>
              <Input
                aria-label={`Replacement ${index + 1}`}
                placeholder="Canonical text"
                value={row.replacement}
                onChange={(e) => update(index, "replacement", e.target.value)}
              />
              <Button
                variant="ghost"
                size="icon"
                disabled={index === 0}
                onClick={() => move(index, -1)}
                aria-label="Move up"
              >
                <ArrowUp className="size-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                disabled={index === rows.length - 1}
                onClick={() => move(index, 1)}
                aria-label="Move down"
              >
                <ArrowDown className="size-4" />
              </Button>
              <Button
                variant="ghost"
                size="icon"
                onClick={() => setRows((r) => r.filter((_, i) => i !== index))}
                aria-label="Remove"
              >
                <Trash2 className="size-4" />
              </Button>
            </div>
          ))
        )}
        <div>
          <Button disabled={busy} onClick={() => void save()}>
            {busy ? "Saving…" : "Save WAIDS map"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
